import React, { useState, useEffect } from 'react';

import { Form, Input, Modal, Button } from 'antd';
import { FormProps } from 'antd/lib/form';
import { useForm } from 'antd/lib/form/Form';
import { Store } from 'antd/lib/form/interface';
import {
  SmsTemplateBasicFragment,
  ListSmsTemplatesDocument,
  SearchSmsTemplatesDocument,
  useCreateSmsTemplateMutation,
  useUpdateSmsTemplateMutation,
} from '../../graphql/generated';
import { getOperationAST } from 'graphql';

interface SMSTemplateFormProps extends FormProps {
  initialSMSTemplate?: SmsTemplateBasicFragment;
  initialValues?: Partial<SmsTemplateBasicFragment>;

  updateMutation?: ReturnType<typeof useUpdateSmsTemplateMutation>[0];
  createMutation?: ReturnType<typeof useCreateSmsTemplateMutation>[0];
  showSubmit?: boolean;
  haveValuesChanged?: boolean;
  setHaveValuesChanged?: React.Dispatch<React.SetStateAction<boolean>>;
}

const SMSTemplateForm: React.FC<SMSTemplateFormProps> = ({
  form,
  initialSMSTemplate,
  initialValues,
  updateMutation,
  createMutation,
  showSubmit,
  haveValuesChanged,
  setHaveValuesChanged,
  ...rest
}) => {
  const [formInternal] = useForm(form);
  const [
    updateMutationInternal,
    updateMutationInternalResults,
  ] = useUpdateSmsTemplateMutation();
  const [
    createMutationInternal,
    createMutationInternalResults,
  ] = useCreateSmsTemplateMutation();

  useEffect(() => {
    formInternal.resetFields();
    formInternal.setFieldsValue(initialSMSTemplate || initialValues);
  }, [formInternal, initialSMSTemplate, initialValues]);
  const formValsToRecordChanges = (formValues: Store) => {
    return initialSMSTemplate
      ? Object.fromEntries(
          Object.entries(formValues).filter(
            ([k, v]) => (initialSMSTemplate as any)[k] !== v
          )
        )
      : formValues;
  };

  const valuesHaveChanged = (formValues: Store) =>
    Object.keys(formValsToRecordChanges(formValues)).length > 0;

  const handleInsert = (values: any) => {
    (createMutation || createMutationInternal)({
      variables: {
        sms_template: { ...formValsToRecordChanges(values) },
      } as any,
      refetchQueries: [
        getOperationAST(ListSmsTemplatesDocument)?.name?.value || '',
        getOperationAST(SearchSmsTemplatesDocument)?.name?.value || '',
      ],
    });
  };

  const handleUpdate = (values: any) => {
    if (!initialSMSTemplate) {
      return;
    }
    const { ...changes } = formValsToRecordChanges(values);
    (updateMutation || updateMutationInternal)({
      variables: {
        sms_template_id: initialSMSTemplate.sms_template_id,
        changes: Object.keys(changes).length ? changes : undefined,
      },
      refetchQueries: [
        getOperationAST(ListSmsTemplatesDocument)?.name?.value || '',
        getOperationAST(SearchSmsTemplatesDocument)?.name?.value || '',
      ],
    });
    if (setHaveValuesChanged) setHaveValuesChanged(false);
  };

  return (
    <Form
      {...rest}
      form={formInternal}
      labelCol={rest.labelCol || { span: 6 }}
      wrapperCol={rest.wrapperCol || { span: 18 }}
      onValuesChange={(_, allValues) =>
        setHaveValuesChanged
          ? setHaveValuesChanged(valuesHaveChanged(allValues))
          : null
      }
      onFinish={initialSMSTemplate ? handleUpdate : handleInsert}
    >
      <Form.Item
        label="Template Name"
        name="sms_template_name"
        rules={[
          {
            required: true,
            message: 'Please provide a template name',
          },
        ]}
      >
        <Input placeholder="Template Name" />
      </Form.Item>
      <Form.Item label="Body Text" name="body_text">
        <Input.TextArea
          placeholder="Message body..."
          autoSize={{ minRows: 4, maxRows: 12 }}
        />
      </Form.Item>
      <Form.Item
        name="webform_url"
        label="Webform URL"
        rules={[{ type: 'url' }]}
      >
        <Input placeholder="https://form.jotform.com/1234..." />
      </Form.Item>
      {showSubmit ? (
        <Form.Item style={{ marginTop: 12 }} wrapperCol={{ offset: 6 }}>
          <Button
            type="primary"
            htmlType="submit"
            disabled={!haveValuesChanged}
            loading={
              createMutationInternalResults.loading ||
              updateMutationInternalResults.loading
            }
          >
            Save
          </Button>
        </Form.Item>
      ) : null}
    </Form>
  );
};

interface SMSTemplateMutationModalProps {
  formName: string;
  initialRecord?: SmsTemplateBasicFragment;
  initialValues?: Partial<SmsTemplateBasicFragment>;
  visible: boolean;
  onCancel?: (() => void) | undefined;
  afterSubmit?: (() => void) | undefined;
}

const SMSTemplateMutationModal: React.FC<SMSTemplateMutationModalProps> = ({
  initialRecord,
  initialValues,
  visible,
  onCancel,
  afterSubmit,
}) => {
  const [form] = useForm();
  const [haveValuesChanged, setHaveValuesChanged] = useState(false);

  const [updateMutation, updateMutationResult] = useUpdateSmsTemplateMutation({
    onCompleted: () => {
      form.resetFields();
      if (afterSubmit) afterSubmit();
    },
  });
  const [createMutation, createMutationResult] = useCreateSmsTemplateMutation({
    onCompleted: () => {
      form.resetFields();
      if (afterSubmit) afterSubmit();
    },
  });
  return (
    <Modal
      title={initialRecord ? 'Update SMS Template' : 'Create SMS Template'}
      okText={initialRecord ? 'Update SMS Template' : 'Create SMS Template'}
      okButtonProps={{ disabled: initialRecord && !haveValuesChanged }}
      visible={visible}
      onCancel={onCancel}
      onOk={(e) => form.submit()}
      confirmLoading={
        updateMutationResult.loading || createMutationResult.loading
      }
    >
      <SMSTemplateForm
        form={form}
        initialSMSTemplate={initialRecord}
        initialValues={initialValues}
        updateMutation={updateMutation}
        createMutation={createMutation}
        haveValuesChanged={haveValuesChanged}
        setHaveValuesChanged={setHaveValuesChanged}
      />
    </Modal>
  );
};

export { SMSTemplateForm, SMSTemplateMutationModal };
